// Loads and parses GTFS .txt files from /src/assets/files/gtfs_extract.
//
// Each .txt is imported as a URL (webpack/CRA asset module). At runtime we
// fetch the URL and parse the CSV. This keeps the source files in /src
// (versioned with the rest of the codebase) without bundling 22 MB of
// stop_times into the JS payload.
//
// Eagerly callable: routes, stops, trips, shapes (≈ 1.5 MB combined).
// Lazy-callable: stop_times (≈ 22 MB) — only invoke when next-stop ETA is
// actually needed.
//
// Results are cached in module scope, so callers can invoke these freely.

import routesUrl from '../../../../../assets/files/gtfs_extract/routes.txt';
import stopsUrl from '../../../../../assets/files/gtfs_extract/stops.txt';
import tripsUrl from '../../../../../assets/files/gtfs_extract/trips.txt';
import shapesUrl from '../../../../../assets/files/gtfs_extract/shapes.txt';
import stopTimesUrl from '../../../../../assets/files/gtfs_extract/stop_times.txt';

const cache = {
  routes: null,
  stops: null,
  trips: null,
  shapes: null,
  stopTimes: null,
  inflight: {}
};

function splitCsvLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/);
  if (lines.length === 0) return [];
  const headers = splitCsvLine(lines[0]);
  const rows = new Array(lines.length - 1);
  let written = 0;
  for (let i = 1; i < lines.length; i += 1) {
    if (!lines[i]) continue;
    const fields = splitCsvLine(lines[i]);
    const obj = {};
    for (let j = 0; j < headers.length; j += 1) {
      obj[headers[j]] = fields[j] !== undefined ? fields[j] : '';
    }
    rows[written] = obj;
    written += 1;
  }
  rows.length = written;
  return rows;
}

async function loadCsv(url, cacheKey) {
  if (cache.inflight[cacheKey]) return cache.inflight[cacheKey];
  const promise = fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`Failed to load ${cacheKey}: ${res.status}`);
      return res.text();
    })
    .then(parseCsv);
  cache.inflight[cacheKey] = promise;
  return promise;
}

export async function loadRoutes() {
  if (cache.routes) return cache.routes;
  const rows = await loadCsv(routesUrl, 'routes');
  cache.routes = rows.map((r) => ({
    routeId: r.route_id,
    shortName: r.route_short_name,
    longName: r.route_long_name,
    color: r.route_color ? `#${r.route_color}` : null
  }));
  return cache.routes;
}

export async function loadStops() {
  if (cache.stops) return cache.stops;
  const rows = await loadCsv(stopsUrl, 'stops');
  cache.stops = rows
    .map((r) => ({
      stopId: r.stop_id,
      name: r.stop_name,
      lat: parseFloat(r.stop_lat),
      lng: parseFloat(r.stop_lon)
    }))
    .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng));
  return cache.stops;
}

export async function loadTrips() {
  if (cache.trips) return cache.trips;
  const rows = await loadCsv(tripsUrl, 'trips');
  cache.trips = rows.map((r) => ({
    tripId: r.trip_id,
    routeId: r.route_id,
    serviceId: r.service_id,
    shapeId: r.shape_id,
    headsign: r.trip_headsign
  }));
  return cache.trips;
}

export async function loadShapes() {
  if (cache.shapes) return cache.shapes;
  const rows = await loadCsv(shapesUrl, 'shapes');
  // Group points by shape_id and sort by sequence so they form a polyline.
  const byShape = new Map();
  for (const r of rows) {
    if (!r.shape_id) continue;
    const lat = parseFloat(r.shape_pt_lat);
    const lng = parseFloat(r.shape_pt_lon);
    const seq = parseInt(r.shape_pt_sequence, 10);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (!byShape.has(r.shape_id)) byShape.set(r.shape_id, []);
    byShape.get(r.shape_id).push({ lat, lng, seq });
  }
  cache.shapes = [];
  byShape.forEach((points, shapeId) => {
    points.sort((a, b) => a.seq - b.seq);
    cache.shapes.push({
      shapeId,
      points: points.map(({ lat, lng }) => ({ lat, lng }))
    });
  });
  return cache.shapes;
}

export async function loadStopTimes() {
  if (cache.stopTimes) return cache.stopTimes;
  const rows = await loadCsv(stopTimesUrl, 'stopTimes');
  cache.stopTimes = rows.map((r) => ({
    tripId: r.trip_id,
    stopId: r.stop_id,
    sequence: parseInt(r.stop_sequence, 10),
    arrivalTime: r.arrival_time,
    departureTime: r.departure_time
  }));
  return cache.stopTimes;
}

export async function loadGtfsCore() {
  const [routes, stops, trips, shapes] = await Promise.all([
    loadRoutes(),
    loadStops(),
    loadTrips(),
    loadShapes()
  ]);
  return { routes, stops, trips, shapes };
}
